import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { fronteggBaseQuery } from '../../fronteggBaseQuery';
import type {
  FronteggPaginationWrapper,
  ISearchUserQueryParamsV3,
  IBaseGetUserResponse,
  ResendUserInvitationEmailPayload,
  IUserRolesResponse,
} from '../types';
import { SortByEnum, PaginationOrderEnum } from '../types';
import { extractOffsetFromLink } from '../../../utils/extractOffsetFromLink';

export type UsersWithRolesPage =
  FronteggPaginationWrapper<IBaseGetUserResponse> & {
    userRoles: IUserRolesResponse[];
  };

export const fronteggUsersApi = createApi({
  baseQuery: fronteggBaseQuery(),
  reducerPath: 'fronteggUsers',
  tagTypes: ['TenantUser'],
  endpoints: (builder) => ({
    getUsers: builder.infiniteQuery<
      FronteggPaginationWrapper<IBaseGetUserResponse>,
      ISearchUserQueryParamsV3 | void,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) =>
          extractOffsetFromLink(lastPage._links?.next),
      },
      query: ({ queryArg, pageParam }) => ({
        url: 'identity/resources/users/v3',
        method: 'GET',
        params: {
          ...(queryArg ?? {}),
          _sortBy: SortByEnum.name,
          _order: PaginationOrderEnum.ASC,
          _offset: pageParam,
          _limit: 200,
        },
      }),
      providesTags: (result) => {
        if (result?.pages) {
          return [
            ...result.pages.flatMap((page) =>
              page.items.map((item) => ({
                type: 'TenantUser' as const,
                id: item.id,
              })),
            ),
            { type: 'TenantUser' as const, id: 'LIST' },
          ];
        }
        return [{ type: 'TenantUser' as const, id: 'LIST' }];
      },
    }),
    getUsersWithRoles: builder.infiniteQuery<
      UsersWithRolesPage,
      ISearchUserQueryParamsV3 | void,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) =>
          extractOffsetFromLink(lastPage._links?.next),
      },
      queryFn: async (
        { queryArg, pageParam },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        const usersResult = await baseQuery({
          url: 'identity/resources/users/v3',
          method: 'GET',
          params: {
            ...(queryArg ?? {}),
            _sortBy: SortByEnum.name,
            _order: PaginationOrderEnum.ASC,
            _offset: pageParam,
            _limit: 200,
          },
        });

        if (usersResult.error) {
          return { error: usersResult.error as FetchBaseQueryError };
        }

        const usersData =
          usersResult.data as FronteggPaginationWrapper<IBaseGetUserResponse>;
        const userIds = usersData.items.map((u) => u.id).filter(Boolean);

        if (userIds.length === 0) {
          return { data: { ...usersData, userRoles: [] } };
        }

        const rolesResult = await baseQuery({
          url: `identity/resources/users/v3/roles?ids=${userIds.join(',')}`,
          method: 'GET',
        });

        if (rolesResult.error) {
          return { error: rolesResult.error as FetchBaseQueryError };
        }

        return {
          data: {
            ...usersData,
            userRoles: rolesResult.data as IUserRolesResponse[],
          },
        };
      },
      providesTags: (result) => {
        if (result?.pages) {
          return [
            ...result.pages.flatMap((page) =>
              page.items.map((item) => ({
                type: 'TenantUser' as const,
                id: item.id,
              })),
            ),
            { type: 'TenantUser' as const, id: 'LIST' },
          ];
        }
        return [{ type: 'TenantUser' as const, id: 'LIST' }];
      },
    }),
    resendInvite: builder.mutation<void, ResendUserInvitationEmailPayload>({
      query: (payload) => ({
        url: 'identity/resources/users/v1/activate/reset',
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const {
  endpoints: { getUsers, getUsersWithRoles, resendInvite },
  useGetUsersInfiniteQuery,
  useGetUsersWithRolesInfiniteQuery,
  useResendInviteMutation,
} = fronteggUsersApi;
